/**
 * IframePluginController is a class responsible for managing the lifecycle and interactions of an iframe within a larger application.
 * It handles the registration and unregistration of objects within the iframe, and manages the iframe's style dynamically.
 */
class IframePluginController {
    /**
     * Constructs an instance of IframePluginController.
     * @param {Object} TaskDataService - Service for handling task data.
     * @param {Object} LogBridgeService - Service for logging across different contexts.
     * @param {Object} $scope - AngularJS scope object.
     * @param {Object} $element - AngularJS element object.
     * @param {Object} $signals - Service for emitting and listening to signals.
     * @param {Object} $bridges - Service for various bridge functionalities.
     * @param {Object} ObjectInstanceRegistrationService - Service for registering and unregistering object instances.
     * @param {Object} $sce - AngularJS Strict Contextual Escaping service.
     */
    constructor(TaskDataService, LogBridgeService, $scope, $element, $signals, $bridges, ObjectInstanceRegistrationService, $sce) {
        // Controller meta setup
        $scope._logIdentifier = 'IframePluginController';
        this.$logger = $bridges.logger();

        // Private scope variables
        this.uuid = $scope.uuid;
        this.src = $scope.src;

        // Public scope variables
        $scope.uuid = this.uuid;
        $scope.styleString = this._createInlineStyle($scope.style);
        $scope.src = $sce.trustAsResourceUrl(this.src);

        // Bind methods to the current instance
        $scope.handleIFrameRegisterObject = this.handleIFrameRegisterObject.bind(this);
        $scope.handleIFrameUnregisterObject = this.handleIFrameUnregisterObject.bind(this);

        // Event bindings
        $element.find('iframe').on('load', () => {
            $element.find('iframe').contents().find('body').attr('uuid', this.uuid);
        });

        $signals.on('iFrameRegisterObject', $scope.handleIFrameRegisterObject);
        $signals.on('iFrameUnregisterObject', $scope.handleIFrameUnregisterObject);

        // Watch for changes in the style object and update the style string accordingly
        $scope.$watch('style', (newval, oldval) => {
            if(newval !== oldval) {
                $scope.styleString = this._createInlineStyle(newval);
            }
        }, true);

        // Clean up event listeners when the scope is destroyed
        $scope.$on('$destroy', () => {
            $signals.remove('iFrameRegisterObject', $scope.handleIFrameRegisterObject);
            $signals.remove('iFrameUnregisterObject', $scope.handleIFrameUnregisterObject);
        });
    }

    /**
     * Converts a style object into a string suitable for inline CSS.
     * @param {Object} style - The style object to convert.
     * @returns {string} - The inline style string.
     */
    _createInlineStyle(style) {
        let arr = [],
            customs = '',
            key, definition;

        for(key in style) {
            if(key === '_customs') {
                customs = style[key];
            } else {
                arr.push([key, ':', style[key] ,';'].join(''));
            }
        }
        arr.push(customs);

        return arr.join('');
    }

    /**
     * Handles the registration of an object within the iframe.
     * @param {Object} evnt - The event object containing the object to register.
     */
    handleIFrameRegisterObject(evnt) {
        if (typeof evnt.object !== 'object') {
            console.error("Cannot register Iframe Object!");
        } else {
            let objectName = evnt.object.constructor.name,
                objectInstance = evnt.object,
                objectUUID = evnt.uuid;

            if(objectUUID === this.uuid){
                ObjectInstanceRegistrationService.register(objectName, objectInstance, objectUUID);
                $signals.emit('iFrameReady', objectUUID);
            }
        }
    }

    /**
     * Handles the unregistration of an object within the iframe.
     * @param {Object} evnt - The event object containing the object to unregister.
     */
    handleIFrameUnregisterObject(evnt) {
        if (typeof evnt !== 'object') {
            console.error("Cannot unregister Iframe Object!");
        } else {
            let objectType = evnt.type,
                objectUUID = evnt.uuid;

            if(objectUUID === this.uuid){
                ObjectInstanceRegistrationService.unregister(objectType, objectUUID);
                $signals.emit('iFrameReady', objectUUID);
            }
        }
    }
}

export default IframePluginController;